const HINDI_DIGITS = /[\u0660-\u0669\u06f0-\u06f9]/gu;
const DISPLAY_ATTRIBUTES = ['aria-label', 'aria-valuetext', 'alt', 'placeholder', 'title'] as const;
const SKIPPED_TEXT_SELECTOR =
  'code,pre,script,style,textarea,input,[contenteditable]:not([contenteditable="false"])';

export function toLatinNumerals(value: string): string {
  return value.replace(HINDI_DIGITS, (digit) => {
    const code = digit.charCodeAt(0);
    return String(code - (code >= 0x06f0 ? 0x06f0 : 0x0660));
  });
}

function isSkippedText(node: Node): boolean {
  return node.parentElement?.closest(SKIPPED_TEXT_SELECTOR) !== null;
}

function normalizeNode(node: Node): void {
  if (node.nodeType === Node.TEXT_NODE) {
    if (isSkippedText(node)) return;
    const text = node.textContent ?? '';
    const normalized = toLatinNumerals(text);
    if (normalized !== text) node.textContent = normalized;
    return;
  }
  if (!(node instanceof Element)) return;
  for (const attribute of DISPLAY_ATTRIBUTES) {
    const value = node.getAttribute(attribute);
    if (value === null) continue;
    const normalized = toLatinNumerals(value);
    if (normalized !== value) node.setAttribute(attribute, normalized);
  }
}

function normalizeSubtree(root: Node): void {
  normalizeNode(root);
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) normalizeNode(walker.currentNode);
}

/** يطبّع النص الديناميكي، بما فيه Radix portals، من دون لمس قيم الحقول أو الروابط. */
export function installLatinNumeralObserver(root: Node): () => void {
  normalizeSubtree(root);
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'characterData') normalizeNode(mutation.target);
      if (mutation.type === 'attributes') normalizeNode(mutation.target);
      for (const node of mutation.addedNodes) normalizeSubtree(node);
    }
  });
  observer.observe(root, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: [...DISPLAY_ATTRIBUTES],
  });
  return () => observer.disconnect();
}
